// App-wide i18n (vue-i18n v11, composition mode). Two locales: en + es.
//
// Message catalogs live in ./locales/<locale>/<namespace>.json — one
// namespace per app area (common, nav, agents, designer, …) so teams/agents
// can add keys without merge conflicts. import.meta.glob assembles them at
// build time; the namespace (file name) becomes the key prefix:
//
//   locales/en/agents.json { "revokeConfirm": "…" }  →  t('agents.revokeConfirm')
//
// Locale resolution: explicit user choice (localStorage 'lymon.locale')
// → browser language (es* → es) → en. The TopBar switcher persists the
// choice and flips the runtime locale without reload.

import { createI18n } from 'vue-i18n';

const STORAGE_KEY = 'lymon.locale';
export const SUPPORTED_LOCALES = ['en', 'es'] as const;
export type AppLocale = (typeof SUPPORTED_LOCALES)[number];

function detectLocale(): AppLocale {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'en' || stored === 'es') return stored;
  } catch {
    /* storage unavailable (some kiosk webviews) — fall through */
  }
  const nav = (navigator.language ?? 'en').toLowerCase();
  return nav.startsWith('es') ? 'es' : 'en';
}

type Messages = Record<string, Record<string, unknown>>;

function loadMessages(): Record<AppLocale, Messages> {
  const out: Record<AppLocale, Messages> = { en: {}, es: {} };
  const modules = import.meta.glob('./locales/*/*.json', { eager: true }) as Record<
    string,
    { default: Record<string, unknown> }
  >;
  for (const [path, mod] of Object.entries(modules)) {
    // ./locales/en/common.json → locale 'en', namespace 'common'
    const m = path.match(/\.\/locales\/(\w+)\/([\w-]+)\.json$/);
    if (!m) continue;
    const [, locale, ns] = m;
    if (locale === 'en' || locale === 'es') {
      out[locale][ns!] = mod.default;
    }
  }
  return out;
}

export const i18n = createI18n({
  legacy: false,
  globalInjection: true, // $t available in every template
  locale: detectLocale(),
  fallbackLocale: 'en',
  messages: loadMessages(),
  // Hardcoded strings are being migrated incrementally; don't spam the
  // console for keys that fall back to en.
  missingWarn: false,
  fallbackWarn: false,
});

export function setLocale(locale: AppLocale): void {
  i18n.global.locale.value = locale;
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    /* best-effort persistence */
  }
  document.documentElement.setAttribute('lang', locale);
}

export function currentLocale(): AppLocale {
  return i18n.global.locale.value as AppLocale;
}

/** t() for non-component code (stores, composables, the design runtime). */
export function t(key: string, params?: Record<string, unknown>): string {
  return params ? i18n.global.t(key, params) : i18n.global.t(key);
}
